"use client";

import { useState, useEffect } from "react";
import MapComponent from "../../Components/Map/IndicatorMap.js";
import ChartComponent from "./ChartComponent.js";
import { useTranslation } from "react-i18next";
import useApi from "../../Hooks/useApi";
import { isAuthenticated } from "../../Helpers/authHelper";
import { fetchYearsForIndicator } from "../../Utils/IndicatorsUtils.js";

const DATA = {
  CWIS: {
    "Access and Equity": [
      { name: "imp_toilet", id: "imp_toilet" },
      { name: "will_imp_toilet", id: "will_imp_toilet" },
      { name: "will_imp_cont", id: "will_imp_cont" },
    ],
    "Environmental Safety": [
      { name: "cont_accessible", id: "cont_accessible" },
    ],
    "Worker Safety": [
      { name: "cont_emptied_mp", id: "cont_emptied_mp" },
      { name: "fs_to_tp", id: "fs_to_tp" },
    ],
  },
  "Solid Waste Management (SWM)": [
    { name: "swm_segregate", id: "swm_segregate" },
    { name: "swm_van_ulb", id: "swm_van_ulb" },
    { name: "swm_van_other", id: "swm_van_other" },
  ],
  "SDG 6.2": [
    { name: "sdg_safemanaged", id: "sdg_safemanaged" },
    { name: "sdg_basic", id: "sdg_basic" },
    { name: "sdg_limited", id: "sdg_limited" },
    { name: "sdg_unimproved", id: "sdg_unimproved" },
    { name: "sdg_opendefication", id: "sdg_opendefication" },
  ],
};

const ToggleSwitch = ({ isOn, onToggle }) => (
  <div
    className={`mt-2 float-left cursor-pointer relative w-[35px] h-[8px] ${isOn ? "border-orange-300" : "border-blue-900"
      } border-[1px] rounded-r-full rounded-l-full`}
    onClick={onToggle}
  >
    <div
      className={`absolute top-1/2 -translate-y-1/2 size-[16px] bg-blue-900 rounded-full transition-all duration-300 ${isOn ? "translate-x-[150%] bg-orange-300" : "-left-1"
        }`}
    ></div>
  </div>
);

const ExpandableComponent = ({
  label,
  options = [],
  selectedOption,
  onOptionToggle,
  isExpanded,
  onToggleExpand,
}) => {
  const { t } = useTranslation();

  return (
    <div className="border-b-2">
      {label && (
        <div
          onClick={onToggleExpand}
          className="cursor-pointer w-full bg-gray-400 flex justify-between items-center px-4 py-2 bg-opacity-15"
        >
          <h3>{t(`indicators.${label}`)}</h3>
          <span className="float-right font-semibold text-2xl">
            {isExpanded ? "-" : "+"}
          </span>
        </div>
      )}
      <div
        className={`overflow-hidden transition-all duration-500 ease-in-out ${!label || isExpanded
          ? "max-h-[1000px] opacity-100"
          : "max-h-0 opacity-0"
          }`}
      >
        <div className="p-8 py-0">
          {options?.map((option) => (
            <div key={option.id} className="w-full text-left py-1">
              <ToggleSwitch
                isOn={selectedOption === option.id}
                onToggle={(e) => {
                  e.stopPropagation();
                  onOptionToggle(option.id);
                }}
              />
              <span className="px-2 text-sm">{t(`indicators.${option.name}`)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function Indicators() {
  const [activeSection, setActiveSection] = useState("CWIS");
  const [expandedSubsection, setExpandedSubsection] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [activeTab, setActiveTab] = useState("map");
  const [selectedYear, setSelectedYear] = useState(null);
  const [years, setYears] = useState([]);
  const { t } = useTranslation();
  const { request } = useApi({ isPublic: !isAuthenticated() });

  useEffect(() => {
    const fetchYears = async () => {
      try {
       await fetchYearsForIndicator(request, setYears);
      } catch (error) {
        console.error("Failed to fetch years:", error);
      }
    };
    fetchYears();
  }, []);

  const handleSectionToggle = (section) => {
    if (activeSection === section) {
      setActiveSection(null);
    } else {
      setActiveSection(section);
      setExpandedSubsection(null);
    }
    setSelectedOption(null);
  };

  const handleSubsectionToggle = (subsection) => {
    setExpandedSubsection(
      expandedSubsection === subsection ? null : subsection
    );
  };

  const handleOptionToggle = (optionId) => {
    setSelectedOption(selectedOption === optionId ? null : optionId);
  };

  return (
    <div className="w-full px-6 !overflow-hidden h-full">
      <div className="flex justify-between h-full">
        {/* Sidebar */}
        <section className="w-[30%] pr-10 h-full overflow-y-scroll">
          <section>
            <h2 className="text-2xl text-left">
              {t("indicators.description")}
            </h2>
            <p className="text-sm text-left pb-4">
              {t("indicators.description_text")}
            </p>
          </section>

          <div className="w-full flex items-center gap-4 bg-slate-50 rounded-md border-2 mb-4 p-3">
            <label className="text-sm font-medium whitespace-nowrap">
              {t("indicators.year_selection_text")}
            </label>

            <select
              value={selectedYear ?? ""}
              onChange={(e) =>
                setSelectedYear(e.target.value === "" ? null : Number(e.target.value))
              }
              className="flex-1 py-2 px-3 rounded-md font-semibold text-sm text-black cursor-pointer outline-none"
              style={{ backgroundColor: "#e2e2e2" }}
            >
              <option value="">{t("indicators.all_years_selected")}</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>


          {/* CWIS Section */}
          <div className="rounded-t-md border-2">
            <div
              onClick={() => handleSectionToggle("CWIS")}
              className="cursor-pointer w-full bg-gray-400 flex justify-between items-center px-4 py-2 bg-opacity-15"
            >
              <h3>{t("indicators.CWIS")}</h3>
              <span className="float-right font-semibold text-2xl">
                {activeSection === "CWIS" ? "-" : "+"}
              </span>
            </div>
            <div
              className={`overflow-hidden transition-all duration-1000 ease-in-out ${activeSection === "CWIS"
                ? "max-h-[2000px] opacity-100"
                : "max-h-0 opacity-0"
                }`}
            >
              <div className="p-4 pr-0">
                {Object.entries(DATA.CWIS).map(([subsection, options]) => (
                  <ExpandableComponent
                    key={subsection}
                    label={subsection}
                    options={options}
                    selectedOption={selectedOption}
                    onOptionToggle={handleOptionToggle}
                    isExpanded={expandedSubsection === subsection}
                    onToggleExpand={() => handleSubsectionToggle(subsection)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* SWM Section */}
          <div className="border-x-2">
            <div
              onClick={() => handleSectionToggle("SWM")}
              className="cursor-pointer bg-gray-400 flex justify-between items-center px-4 py-2 bg-opacity-15"
            >
              <h3>{t("indicators.SWM")}</h3>
              <span className="float-right font-semibold text-2xl">
                {activeSection === "SWM" ? "-" : "+"}
              </span>
            </div>
            <div
              className={`overflow-hidden transition-all duration-500 ease-in-out ${activeSection === "SWM"
                ? "max-h-[1000px] opacity-100"
                : "max-h-0 opacity-0"
                }`}
            >
              <div>
                <ExpandableComponent
                  options={DATA["Solid Waste Management (SWM)"]}
                  selectedOption={selectedOption}
                  onOptionToggle={handleOptionToggle}
                  isExpanded={true}
                />
              </div>
            </div>
          </div>

          {/* SDG Section */}
          <div className="rounded-b-md border-2">
            <div
              onClick={() => handleSectionToggle("SDG")}
              className="cursor-pointer bg-gray-400 flex justify-between items-center px-4 py-2 bg-opacity-15"
            >
              <h3>{t("indicators.SDG 6.2")}</h3>
              <span className="float-right font-semibold text-2xl">
                {activeSection === "SDG" ? "-" : "+"}
              </span>
            </div>
            <div
              className={`overflow-hidden transition-all duration-500 ease-in-out ${activeSection === "SDG"
                ? "max-h-[1000px] opacity-100"
                : "max-h-0 opacity-0"
                }`}
            >
              <div>
                <ExpandableComponent
                  options={DATA["SDG 6.2"]}
                  selectedOption={selectedOption}
                  onOptionToggle={handleOptionToggle}
                  isExpanded={true}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Map/Chart Render Section */}
        <section className="w-[70%] pl-2 pt-2">
          <div className="flex justify-between mb-2">
            <div className="flex-1"></div>
            <div className="flex rounded-md overflow-hidden w-full">
              <div
                className={`px-8 py-2 w-full cursor-pointer ${activeTab === "chart"
                  ? "border-t-[1px] border-l-[1px] border-r-[1px] text-blue-500 bg-gray-400 bg-opacity-20 rounded-tr-md"
                  : "border-b-[1px] rounded-br-md"
                  }`}
                onClick={() => setActiveTab("chart")}
              >
                {t("indicators.chart")}
              </div>
              <div
                className={`px-8 py-2 w-full cursor-pointer ${activeTab === "map"
                  ? "border-t-[1px] border-l-[1px] text-blue-500 bg-gray-400 bg-opacity-20 rounded-tl-md"
                  : "border-b-[1px] rounded-bl-md"
                  }`}
                onClick={() => setActiveTab("map")}
              >
                {t("indicators.map")}
              </div>
            </div>
          </div>

          <div className="h-[73vh] w-full border-2">
            {activeTab === "map" ? (
              <MapComponent indicators={selectedOption} year={selectedYear} />
            ) : (
              <ChartComponent selectedIndicator={selectedOption} year={selectedYear} />
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
